import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, X, Save } from "lucide-react";
import { adminGetSettings, adminUpdateSettings } from "@/lib/api";
import { ImageUpload } from "@/admin/FormFields";

export default function SettingsEditor() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings-admin"], queryFn: adminGetSettings });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data, setForm]);

  const mut = useMutation({
    mutationFn: (d) => adminUpdateSettings(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
    onError: () => toast.error("Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socialLinks: { ...(f.socialLinks || {}), [k]: v } }));
  const setCounter = (i, k, v) => setForm((f) => {
    const arr = [...(f.impactCounters || [])];
    arr[i] = { ...arr[i], [k]: k === "value" ? Number(v) : v };
    return { ...f, impactCounters: arr };
  });

  const inp = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none text-sm";

  return (
    <div data-testid="settings-editor">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">Site Settings</h1>
        <button onClick={() => mut.mutate(form)} disabled={mut.isPending} data-testid="save-settings"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-60">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>

      <div className="space-y-6">
        <Card title="Hero Section">
          <Row label="Hero Title"><input value={form.heroTitle || ""} onChange={(e) => set("heroTitle", e.target.value)} className={inp} /></Row>
          <Row label="Hero Title (Kannada)"><input value={form.heroTitle_kn || ""} onChange={(e) => set("heroTitle_kn", e.target.value)} className={inp} /></Row>
          <Row label="Subtitle"><input value={form.heroSubtitle || ""} onChange={(e) => set("heroSubtitle", e.target.value)} className={inp} /></Row>
          <Row label="Tagline"><input value={form.heroTagline || ""} onChange={(e) => set("heroTagline", e.target.value)} className={inp} /></Row>
          <Row label="Description"><textarea rows={3} value={form.heroDescription || ""} onChange={(e) => set("heroDescription", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Portrait Image"><ImageUpload value={form.heroImage} onChange={(v) => set("heroImage", v)} /></Row>
        </Card>

        <Card title="About Page Content">
          <Row label="Introduction"><textarea rows={3} value={form.aboutIntro || ""} onChange={(e) => set("aboutIntro", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Journey"><textarea rows={3} value={form.aboutJourney || ""} onChange={(e) => set("aboutJourney", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Philosophy"><textarea rows={2} value={form.aboutPhilosophy || ""} onChange={(e) => set("aboutPhilosophy", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Vision"><textarea rows={2} value={form.aboutVision || ""} onChange={(e) => set("aboutVision", e.target.value)} className={`${inp} resize-none`} /></Row>
        </Card>

        <Card title="Impact Counters">
          {(form.impactCounters || []).map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-6"><label className="text-xs text-slate-400">Label</label><input value={c.label} onChange={(e) => setCounter(i, "label", e.target.value)} className={inp} /></div>
              <div className="col-span-3"><label className="text-xs text-slate-400">Value</label><input type="number" value={c.value} onChange={(e) => setCounter(i, "value", e.target.value)} className={inp} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Suffix</label><input value={c.suffix || ""} onChange={(e) => setCounter(i, "suffix", e.target.value)} className={inp} /></div>
              <button onClick={() => set("impactCounters", form.impactCounters.filter((_, idx) => idx !== i))} className="col-span-1 h-9 w-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => set("impactCounters", [...(form.impactCounters || []), { label: "", value: 0, suffix: "" }])} className="inline-flex items-center gap-1.5 text-sm text-navy font-medium"><Plus className="h-4 w-4" /> Add counter</button>
        </Card>

        <Card title="Contact Details">
          <Row label="Phone"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inp} /></Row>
          <Row label="WhatsApp"><input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inp} /></Row>
          <Row label="Email"><input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inp} /></Row>
          <Row label="Address"><textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={`${inp} resize-none`} /></Row>
          <Row label="Google Maps Embed URL"><input value={form.mapEmbed || ""} onChange={(e) => set("mapEmbed", e.target.value)} className={inp} /></Row>
        </Card>

        <Card title="Social Links">
          {["facebook", "instagram", "twitter", "youtube", "linkedin", "threads"].map((s) => (
            <Row key={s} label={s[0].toUpperCase() + s.slice(1)}><input value={form.socialLinks?.[s] || ""} onChange={(e) => setSocial(s, e.target.value)} className={inp} data-testid={`settings-social-${s}`} /></Row>
          ))}
          <p className="text-xs text-slate-400">Leave blank to fall back to the links saved in the Digital Card editor — the footer shows both.</p>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h2 className="font-display text-lg font-semibold text-navy mb-5">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
      {children}
    </div>
  );
}
